import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmDialog } from "../../utils/confirmDialog";
import {
  CATEGORIES,
  LOCATION_RULES,
  categories,
  codes,
  effectiveLocation,
  excelRoles,
  findNode,
  isLinked,
  libraryTree,
  numberedObjects,
  presetTree,
  sheetList,
  tabPrefix,
} from "../../structure/structureRules";
import {
  TreeEditError,
  blankNode,
  duplicateNode,
  indentNode,
  insertNode,
  moveDestinations,
  moveInto,
  moveNode,
  outdentNode,
  placeFromLibrary,
  plusTarget,
  removeNodes,
  updateNode,
  usedLibIds,
} from "../../structure/treeOps";

const LIBRARY_KINDS = [
  { key: "building", label: "შენობა — საცხოვრებელი / კომერციული" },
  { key: "site", label: "ნაკვეთის საერთო სამუშაოები" },
  { key: "parking", label: "მიწისქვეშა პარკინგი" },
];
const SHORT_CATEGORY = { hard: "CAPEX · პირდაპირი (Hard Cost)", soft: "CAPEX · არაპირდაპირი (Soft Cost)", opex: "OPEX · საოპერაციო" };

const textOf = (n) => n.name.toLowerCase();

/** Visible rows in display order, with depth; collapsed branches are skipped unless searching. */
function visibleRows(tree, collapsed, query, rowCodes) {
  const q = query.trim().toLowerCase();
  const hit = (n) => textOf(n).includes(q) || (rowCodes[n.id] || "").startsWith(q);
  const keep = (n) => hit(n) || n.kids.some(keep);
  const out = [];
  const walk = (list, depth, parent) =>
    list.forEach((n) => {
      if (q && !keep(n)) return;
      out.push({ node: n, depth, parent });
      if (n.kids.length && (q || !collapsed.has(n.id))) walk(n.kids, depth + 1, n);
    });
  walk(tree, 1, null);
  return out;
}

export default function StructureEditor({
  site,
  trees,
  frozen,
  template,
  savedTemplates,
  isSuperuser,
  onTreeChange,
  onSameStructure,
  onSaveTemplate,
  onSaveSystemTemplate,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
}) {
  const objects = numberedObjects(site);
  const [cur, setCur] = useState(objects[0]?.id);
  const [sel, setSel] = useState(null);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [libQuery, setLibQuery] = useState("");
  const [libKind, setLibKind] = useState("building");
  const [renaming, setRenaming] = useState(null);
  const [menu, setMenu] = useState(null);
  const [tplMenu, setTplMenu] = useState(false);
  const [tplName, setTplName] = useState("");
  const [msg, setMsg] = useState(null);
  const treeRef = useRef(null);

  const curObj = objects.find((o) => o.id === cur) || objects[0];
  useEffect(() => {
    if (!objects.some((o) => o.id === cur)) setCur(objects[0]?.id);
  }, [objects, cur]);

  const linked = curObj && isLinked(site, curObj);
  const sourceId = linked ? curObj.typ : curObj?.id;
  const tree = trees[sourceId] || [];
  const rowCodes = useMemo(() => codes(tree, frozen), [tree, frozen]);
  const roles = useMemo(() => excelRoles(tree), [tree]);
  const cats = useMemo(() => categories(tree), [tree]);
  const sheets = useMemo(() => sheetList(tree, rowCodes, tabPrefix(site, curObj?.number ?? 1)), [tree, rowCodes, site, curObj]);
  const sheetById = useMemo(() => Object.fromEntries(sheets.map((s) => [s.id, s])), [sheets]);
  const sheetErrors = sheets.filter((s) => s.err);
  const rows = useMemo(() => visibleRows(tree, collapsed, query, rowCodes), [tree, collapsed, query, rowCodes]);
  const library = useMemo(() => {
    if (!template) return [];
    return libraryTree(libKind === "building" ? template : { tree: kindLibrary(template, libKind) });
  }, [template, libKind]);
  const used = useMemo(() => usedLibIds(tree), [tree]);
  const blds = site.ents.filter((e) => e.kind === "bldg");
  const selHit = sel ? findNode(tree, sel) : null;

  const say = (text, kind = "ok") => setMsg({ text, kind });
  const apply = (fn, nextSel) => {
    try {
      const next = fn(tree);
      onTreeChange(sourceId, next);
      if (nextSel !== undefined) setSel(nextSel);
      setMsg(null);
      return true;
    } catch (err) {
      if (err instanceof TreeEditError) say(err.message, "err");
      else throw err;
      return false;
    }
  };

  const addAt = (id) => {
    const target = plusTarget(tree, id);
    const node = blankNode(target.sys ? "ახალი სისტემა" : "ახალი ქვე-დისციპლინა", target.sys);
    if (apply((t) => insertNode(t, target.parentId, target.index, node), node.id)) setRenaming({ id: node.id, draft: node.name });
  };
  const addChild = (id, sys) => {
    const hit = findNode(tree, id);
    const node = blankNode(sys ? "ახალი სისტემა" : "ახალი ქვე-დისციპლინა", sys);
    if (apply((t) => insertNode(t, id, hit.node.kids.length, node), node.id)) setRenaming({ id: node.id, draft: node.name });
  };
  const addTop = () => {
    const node = blankNode("ახალი დისციპლინა");
    if (apply((t) => insertNode(t, null, t.length, node), node.id)) setRenaming({ id: node.id, draft: node.name });
  };
  const commitRename = () => {
    if (!renaming) return;
    const name = renaming.draft.trim();
    if (name) apply((t) => updateNode(t, renaming.id, { name }));
    setRenaming(null);
  };
  const askDelete = async (id) => {
    const hit = findNode(tree, id);
    if (!hit) return;
    const size = (n) => 1 + n.kids.reduce((s, k) => s + size(k), 0);
    const count = size(hit.node);
    const ok = await confirmDialog(
      count > 1 ? `წაიშალოს „${hit.node.name}“ და მისი ${count - 1} ქვე-სტრიქონი?` : `წაიშალოს „${hit.node.name}“?`,
      { confirmLabel: "წაშლა", cancelLabel: "გაუქმება" }
    );
    if (ok) apply((t) => removeNodes(t, [id]), hit.parent?.id ?? null);
  };
  const toggleOpen = (id, open) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (open ?? next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const collapseAll = () => {
    const all = new Set();
    const walk = (list) => list.forEach((n) => n.kids.length && (all.add(n.id), walk(n.kids)));
    walk(tree);
    setCollapsed(all);
  };

  const addFromLibrary = (libNode, depth) => {
    try {
      const r = placeFromLibrary(tree, libNode, depth, sel);
      onTreeChange(sourceId, r.tree);
      setSel(libNode.id);
      say(r.message);
    } catch (err) {
      if (err instanceof TreeEditError) say(err.message, "err");
      else throw err;
    }
  };

  const loadTemplate = async (label, nextTree) => {
    if (tree.length && !(await confirmDialog(`„${label}“ ჩაანაცვლებს „${curObj.name}“-ის მიმდინარე სტრუქტურას. გავაგრძელოთ?`, { confirmLabel: "ჩატვირთვა", cancelLabel: "გაუქმება", danger: false })))
      return;
    onTreeChange(sourceId, JSON.parse(JSON.stringify(nextTree)));
    setSel(null);
    setTplMenu(false);
    say(`„${label}“ ჩაიტვირთა.`);
  };
  const canBeDefault = !!curObj && curObj.kind === "bldg" && blds.length === 1;
  const presetForObject = () =>
    presetTree(template, curObj.kind === "bldg" ? "building" : curObj.tpl, { single: blds.length === 1 });

  const onKeyDown = (e) => {
    if (renaming) return;
    const idx = rows.findIndex((r) => r.node.id === sel);
    const ctrl = e.ctrlKey || e.metaKey;
    if (ctrl && e.code === "KeyZ") return e.preventDefault(), onUndo();
    if (ctrl && e.code === "KeyY") return e.preventDefault(), onRedo();
    if (!selHit) {
      if (e.key === "ArrowDown" && rows.length) setSel(rows[0].node.id);
      return;
    }
    if (e.altKey && e.shiftKey) {
      const ops = { ArrowUp: (t) => moveNode(t, sel, -1), ArrowDown: (t) => moveNode(t, sel, 1), ArrowLeft: (t) => outdentNode(t, sel), ArrowRight: (t) => indentNode(t, sel) };
      if (ops[e.key]) return e.preventDefault(), apply(ops[e.key]);
    }
    if (ctrl && e.code === "KeyD") return e.preventDefault(), apply((t) => duplicateNode(t, sel));
    if (e.key === "ArrowDown" && idx < rows.length - 1) setSel(rows[idx + 1].node.id);
    else if (e.key === "ArrowUp" && idx > 0) setSel(rows[idx - 1].node.id);
    else if (e.key === "ArrowLeft") {
      if (selHit.node.kids.length && !collapsed.has(sel)) toggleOpen(sel, false);
      else if (selHit.parent) setSel(selHit.parent.id);
    } else if (e.key === "ArrowRight") toggleOpen(sel, true);
    else if (e.key === "F2") setRenaming({ id: sel, draft: selHit.node.name });
    else if (e.key === "Delete") askDelete(sel);
    else return;
    e.preventDefault();
  };

  const menuItems = (id) => {
    const hit = findNode(tree, id);
    if (!hit) return [];
    const n = hit.node;
    const holdsSys = n.kids.length && n.kids[0].sys;
    const holdsDisc = n.kids.length && !n.kids[0].sys;
    return [
      { label: "სახელის გადარქმევა", key: "F2", run: () => setRenaming({ id, draft: n.name }) },
      { sep: true },
      { label: "ქვე-დისციპლინა", off: n.sys || holdsSys, run: () => addChild(id, false) },
      { label: "სისტემა", off: !n.sys && (holdsDisc || !hit.parent), run: () => (n.sys ? addAt(id) : addChild(id, true)) },
      { label: "დუბლირება", key: "Ctrl+D", run: () => apply((t) => duplicateNode(t, id)) },
      { sep: true },
      { label: "ზემოთ", key: "Alt+Shift+↑", off: hit.index === 0, run: () => apply((t) => moveNode(t, id, -1)) },
      { label: "ქვემოთ", key: "Alt+Shift+↓", off: hit.index === hit.list.length - 1, run: () => apply((t) => moveNode(t, id, 1)) },
      { label: "დონით ზემოთ", key: "Alt+Shift+←", off: !hit.parent, run: () => apply((t) => outdentNode(t, id)) },
      { label: "დონით ქვემოთ", key: "Alt+Shift+→", off: hit.index === 0, run: () => apply((t) => indentNode(t, id)) },
      { label: "გადატანა…", run: () => setMenu({ id, moveTo: true, x: menu?.x, y: menu?.y }), keep: true },
      { sep: true },
      { label: n.hidden ? "გამოჩენა Excel-ში" : "დამალვა Excel-ში", run: () => apply((t) => updateNode(t, id, { hidden: !n.hidden })) },
      { label: n.on === false ? "ჩართვა" : "გამორთვა (ნომრის გარეშე)", run: () => apply((t) => updateNode(t, id, { on: n.on === false })) },
      { sep: true },
      { label: "წაშლა", key: "Delete", danger: true, run: () => askDelete(id) },
    ];
  };

  const objTab = (o) => {
    const linkedTo = isLinked(site, o) ? objects.find((x) => x.id === o.typ) : null;
    return (
      <button key={o.id} type="button" className={`otab${o.id === curObj?.id ? " on" : ""}`} onClick={() => (setCur(o.id), setSel(null))}>
        <span>
          {o.number} · {o.name}
        </span>
        <small>{linkedTo ? `= ${linkedTo.number}` : o.kind === "common" ? "საერთო" : o.kind === "shared" ? "პარკინგი" : "შენობა"}</small>
      </button>
    );
  };

  const selNode = selHit?.node;
  const selLoc = selNode && !selNode.sys ? effectiveLocation(tree, selNode.id) : null;
  const inheritedLoc = selHit?.parent ? effectiveLocation(tree, selHit.parent.id) : "none";
  const selSheet = selNode ? sheetById[selNode.id] : null;

  return (
    <>
      <div className="tbar">
        {objects.length > 1 && objects.map(objTab)}
        {blds.length > 1 && (
          <button type="button" className={`swb${site.sameStructure ? " on" : ""}`} onClick={() => onSameStructure(!site.sameStructure)}>
            <span className="sw" />
            ერთი სტრუქტურა ყველა შენობისთვის
          </button>
        )}
        <span className="grow" />
        <button type="button" className="ib" disabled={!canUndo} onClick={onUndo} title="დაბრუნება (Ctrl+Z)">
          ↶
        </button>
        <button type="button" className="ib" disabled={!canRedo} onClick={onRedo} title="გამეორება (Ctrl+Y)">
          ↷
        </button>
        <div style={{ position: "relative" }}>
          <button type="button" className="btn" disabled={linked} onClick={() => setTplMenu((v) => !v)}>
            შაბლონი ▾
          </button>
          {tplMenu && (
            <>
              <div className="veil" onClick={() => setTplMenu(false)} />
              <div className="menu" style={{ right: 18, top: 92 }}>
                <div className="lbl">ჩატვირთვა</div>
                <button type="button" onClick={() => loadTemplate(template?.name || "საწყისი შაბლონი", presetForObject())}>
                  {template?.name || "QoBIM R04"}
                </button>
                {savedTemplates
                  .filter((t) => t.scope === "COMPANY")
                  .map((t) => (
                    <button key={t.id} type="button" onClick={() => loadTemplate(t.name, t.tree)}>
                      {t.name} <span className="key">კომპანია</span>
                    </button>
                  ))}
                {savedTemplates
                  .filter((t) => t.scope === "OWN")
                  .map((t) => (
                    <button key={t.id} type="button" onClick={() => loadTemplate(t.name, t.tree)}>
                      {t.name} <span className="key">ჩემი</span>
                    </button>
                  ))}
                <div className="sep" />
                <div className="lbl">შაბლონად შენახვა</div>
                <div style={{ padding: "2px 12px 6px" }}>
                  <input className="inp" placeholder="დასახელება" value={tplName} onChange={(e) => setTplName(e.target.value)} />
                </div>
                <button
                  type="button"
                  disabled={!tplName.trim()}
                  onClick={async () => {
                    await onSaveTemplate({ name: tplName.trim(), scope: "OWN", tree });
                    setTplName("");
                    setTplMenu(false);
                    say("შაბლონი შეინახა.");
                  }}
                >
                  ჩემს შაბლონებად
                </button>
                <button
                  type="button"
                  disabled={!tplName.trim() || !isSuperuser}
                  onClick={async () => {
                    await onSaveTemplate({ name: tplName.trim(), scope: "COMPANY", tree });
                    setTplName("");
                    setTplMenu(false);
                    say("კომპანიის შაბლონი შეინახა.");
                  }}
                >
                  კომპანიის შაბლონად <span className="key">ადმინისტრატორი</span>
                </button>
                {isSuperuser && (
                  <button
                    type="button"
                    disabled={!canBeDefault}
                    title={canBeDefault ? "ყველა ახალი პროექტი ამ სტრუქტურით დაიწყება" : "მხოლოდ ერთშენობიანი პროექტის შენობიდან — მასში არის მთელი შაბლონი"}
                    onClick={async () => {
                      if (!(await confirmDialog("ეს სტრუქტურა გახდება ნაგულისხმევი: ყველა ახალი პროექტი მისით დაიწყება. გავაგრძელოთ?", { confirmLabel: "შენახვა", cancelLabel: "გაუქმება", danger: false })))
                        return;
                      setTplMenu(false);
                      if (await onSaveSystemTemplate(tree)) say("ნაგულისხმევი შაბლონი განახლდა.");
                    }}
                  >
                    ნაგულისხმევ შაბლონად (ყველასთვის) <span className="key">ადმინისტრატორი</span>
                  </button>
                )}
                <div className="sep" />
                <button type="button" onClick={() => loadTemplate("საწყისი შაბლონი", presetForObject())}>
                  საწყის შაბლონზე დაბრუნება
                </button>
                <button type="button" className="danger" onClick={() => loadTemplate("ცარიელი სტრუქტურა", [])}>
                  სტრუქტურის გასუფთავება
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {linked ? (
        <div style={{ padding: 18 }}>
          <div className="msg warn">
            „{curObj.name}“ = „{objects.find((o) => o.id === curObj.typ)?.name}“: სტრუქტურა და სამუშაოები საერთოა, Excel-ში ცალკე ობიექტია.
            <button type="button" className="btn xs" onClick={() => setCur(curObj.typ)}>
              წყაროს გახსნა
            </button>
            <button type="button" className="btn xs" onClick={() => onSameStructure(false)}>
              გათიშვა — დამოუკიდებელი ასლი
            </button>
          </div>
        </div>
      ) : (
        <div className="sbody">
          <div className="col">
            <div className="ttools">
              <div className="search">
                <span aria-hidden="true">⌕</span>
                <input value={query} placeholder="ძებნა სახელით ან ნომრით" onChange={(e) => setQuery(e.target.value)} />
                {query && (
                  <button type="button" className="qx" onClick={() => setQuery("")}>
                    ✕
                  </button>
                )}
              </div>
              <button type="button" className="btn xs" onClick={() => setCollapsed(new Set())}>
                ყველას გაშლა
              </button>
              <button type="button" className="btn xs" onClick={collapseAll}>
                ყველას დაკეცვა
              </button>
            </div>

            <div className="tree" tabIndex={0} ref={treeRef} onKeyDown={onKeyDown} data-no-translate="true">
              <div className="thd">
                <span style={{ width: 16 }} />
                <span className="tname">დასახელება</span>
                <span style={{ width: 24, textAlign: "center" }}>ჩანს</span>
              </div>
              {!tree.length && <div className="tempty">სტრუქტურა ცარიელია. დაამატეთ დისციპლინები ბიბლიოთეკიდან (+) ან ჩატვირთეთ შაბლონი.</div>}
              {tree.length > 0 && !rows.length && <div className="tempty">„{query}“ — ვერაფერი მოიძებნა.</div>}
              {rows.map(({ node: n, depth }, i) => {
                const prevTop = rows.slice(0, i).reverse().find((r) => r.depth === 1);
                const band = depth === 1 && (!prevTop || cats[prevTop.node.id] !== cats[n.id]) ? cats[n.id] : null;
                const sheet = sheetById[n.id];
                const off = n.on === false;
                return (
                  <React.Fragment key={n.id}>
                    {band && <div className="catband">{SHORT_CATEGORY[band]}</div>}
                    <div
                      className={`tr${sel === n.id ? " sel" : ""}${off ? " off" : ""}${n.hidden ? " hid" : ""}`}
                      style={{ paddingLeft: 8 + (depth - 1) * 18 }}
                      onClick={() => {
                        setSel(n.id);
                        treeRef.current?.focus();
                      }}
                      onDoubleClick={() => setRenaming({ id: n.id, draft: n.name })}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setSel(n.id);
                        setMenu({ id: n.id, x: e.clientX, y: e.clientY });
                      }}
                    >
                      <button
                        type="button"
                        className={`tw${n.kids.length ? "" : " none"}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleOpen(n.id);
                        }}
                        aria-label={collapsed.has(n.id) ? "გაშლა" : "დაკეცვა"}
                      >
                        {collapsed.has(n.id) && !query ? "▶" : "▼"}
                      </button>
                      <span className="code">{rowCodes[n.id] || "—"}</span>
                      {n.sys && <span className="sysm">▣</span>}
                      <span className="tname">
                        {renaming?.id === n.id ? (
                          <input
                            autoFocus
                            value={renaming.draft}
                            onChange={(e) => setRenaming({ ...renaming, draft: e.target.value })}
                            onBlur={commitRename}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitRename();
                              if (e.key === "Escape") setRenaming(null);
                              e.stopPropagation();
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          n.name
                        )}
                      </span>
                      {n.hidden && <span className="tag">დამალულია</span>}
                      {sheet && <span className={`tag ${sheet.err ? "bad" : "sheet"}`} title={sheet.err || "Excel-ის ფურცელი"}>{sheet.tab}</span>}
                      <button
                        type="button"
                        className="ra"
                        title="დამატება"
                        onClick={(e) => {
                          e.stopPropagation();
                          addAt(n.id);
                        }}
                      >
                        +
                      </button>
                      <button
                        type="button"
                        className="ra"
                        title="მენიუ"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSel(n.id);
                          const r = e.currentTarget.getBoundingClientRect();
                          setMenu({ id: n.id, x: r.left, y: r.bottom + 2 });
                        }}
                      >
                        ⋯
                      </button>
                      <button
                        type="button"
                        className={`eye${n.hidden ? " off" : ""}`}
                        title={n.hidden ? "დამალულია — გამოჩენა" : "დამალვა Excel-ში"}
                        onClick={(e) => {
                          e.stopPropagation();
                          apply((t) => updateNode(t, n.id, { hidden: !n.hidden }));
                        }}
                      >
                        {n.hidden ? "⦸" : "◉"}
                      </button>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            <div className="treefoot">
              <button type="button" className="linkbtn" onClick={addTop}>
                + დისციპლინა
              </button>
              <span className={`snote${sheetErrors.length ? " bad" : ""}`}>
                {sheetErrors.length
                  ? `${sheetErrors.length} ფურცლის სახელი შესასწორებელია`
                  : `${sheets.length} ფურცელი + ${objects.length > 1 ? `${curObj.number}-TOTAL` : "TOTAL"} · დამალული ხაზი Excel-ში არ გამოვა`}
              </span>
            </div>

            {msg && <div className={`msg ${msg.kind}`}>{msg.text}</div>}

            {selNode && (
              <div className="rset" data-no-translate="true">
                <div className="rset-h">
                  <span>
                    <b>{selNode.name}</b> ·{" "}
                    {selNode.sys ? "სისტემა (Excel-ში ბლოკი)" : selHit.depth === 1 ? "დისციპლინა (TOTAL-ის ხაზი)" : `ქვე-დისციპლინა, დონე ${selHit.depth}`} ·{" "}
                    {rowCodes[selNode.id] || "—"}
                    {roles[selNode.id] === "sheet" && ` · ფურცელი ${selSheet?.tab}`}
                    {roles[selNode.id] === "heading" && " · სათაური ფურცელში"}
                  </span>
                  <button type="button" className="qx" onClick={() => setSel(null)}>
                    ✕
                  </button>
                </div>
                {!selNode.sys && selNode.kids.some((k) => !k.sys) && (
                  <div className="prow">
                    <label>ქვე-დისციპლინები Excel-ში:</label>
                    <div className="pseg">
                      <button type="button" className={selNode.split !== false ? "on" : ""} onClick={() => apply((t) => updateNode(t, sel, { split: true }))}>
                        ცალკე ფურცლებზე
                      </button>
                      <button type="button" className={selNode.split === false ? "on" : ""} onClick={() => apply((t) => updateNode(t, sel, { split: false }))}>
                        ერთ ფურცელზე
                      </button>
                    </div>
                  </div>
                )}
                {selHit.depth === 1 && (
                  <div className="prow">
                    <label>კატეგორია:</label>
                    <select className="inp" value={cats[sel]} onChange={(e) => apply((t) => updateNode(t, sel, { cat: e.target.value }))}>
                      {Object.entries(CATEGORIES).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                    {!selNode.cat && <span className="snote">ზედა ხაზისგან</span>}
                  </div>
                )}
                {!selNode.sys && (
                  <div className="prow">
                    <label>ფურცლის კოდი:</label>
                    <input
                      className="inp"
                      style={{ minWidth: 90, width: 90 }}
                      value={selNode.sheet || ""}
                      maxLength={12}
                      onChange={(e) => apply((t) => updateNode(t, sel, { sheet: e.target.value.toUpperCase() }))}
                    />
                    {selSheet && (
                      <span className={`snote${selSheet.err ? " bad" : ""}`}>
                        {selSheet.tab} · {selSheet.tab.length}/31{selSheet.err ? ` · ${selSheet.err}` : ""}
                      </span>
                    )}
                  </div>
                )}
                {!selNode.sys && (
                  <div className="prow">
                    <label>ლოკაცია:</label>
                    <select className="inp" value={selNode.loc || ""} onChange={(e) => apply((t) => updateNode(t, sel, { loc: e.target.value }))}>
                      <option value="">{selHit.depth === 1 ? "ობიექტი (ნაგულისხმევი)" : `მემკვიდრეობით: ${LOCATION_RULES[inheritedLoc]}`}</option>
                      {Object.entries(LOCATION_RULES).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                    <span className="snote">სამუშაოს დამატება ჰკითხავს: {LOCATION_RULES[selLoc]}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="col">
            <div className="libhead">
              <span>ბიბლიოთეკა</span>
              <select className="inp" style={{ width: "auto" }} value={libKind} onChange={(e) => setLibKind(e.target.value)}>
                {LIBRARY_KINDS.map((k) => (
                  <option key={k.key} value={k.key}>
                    {k.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="search">
              <span aria-hidden="true">⌕</span>
              <input value={libQuery} placeholder="ბიბლიოთეკაში ძებნა" onChange={(e) => setLibQuery(e.target.value)} />
            </div>
            <div className="tree" data-no-translate="true">
              <LibraryRows nodes={library} depth={1} used={used} query={libQuery.trim().toLowerCase()} onAdd={addFromLibrary} />
            </div>
            <div className="snote">„+“ ამატებს მონიშნულ ხაზთან: შიგნით, თუ იქ ეტევა, ან მის შემდეგ. ბიბლიოთეკა იცვლება: მონაცემთა ბაზები → „სტრუქტურის ბიბლიოთეკა“.</div>
          </div>
        </div>
      )}

      {menu && (
        <>
          <div className="veil" onClick={() => setMenu(null)} onContextMenu={(e) => (e.preventDefault(), setMenu(null))} />
          <div className="menu" style={{ left: Math.min(menu.x, window.innerWidth - 250), top: Math.min(menu.y, window.innerHeight - 380) }}>
            {menu.moveTo ? (
              <>
                <div className="lbl">გადატანა — სად?</div>
                {moveDestinations(tree, menu.id).map((d) => (
                  <button
                    key={d.id ?? "root"}
                    type="button"
                    style={{ paddingLeft: 12 + d.depth * 12 }}
                    onClick={() => {
                      apply((t) => moveInto(t, menu.id, d.id));
                      setMenu(null);
                    }}
                  >
                    {d.id ? `${rowCodes[d.id] || ""} ${findNode(tree, d.id).node.name}` : "ზედა დონე"}
                  </button>
                ))}
              </>
            ) : (
              menuItems(menu.id).map((item, i) =>
                item.sep ? (
                  <div key={`s${i}`} className="sep" />
                ) : (
                  <button
                    key={item.label}
                    type="button"
                    disabled={item.off}
                    className={item.danger ? "danger" : ""}
                    onClick={() => {
                      if (!item.keep) setMenu(null);
                      item.run();
                    }}
                  >
                    {item.label}
                    {item.key && <span className="key">{item.key}</span>}
                  </button>
                )
              )
            )}
          </div>
        </>
      )}
    </>
  );
}

/** Library of a non-building object kind: the template rows of its preset (with optional systems). */
function kindLibrary(template, kind) {
  if (kind === "parking") return template.parking_tree || [];
  const keep = template.presets?.common_object_0 || [];
  return (template.tree || []).filter((t) => keep.includes(t.id));
}

function LibraryRows({ nodes, depth, used, query, onAdd }) {
  const matches = (n) => !query || n.name.toLowerCase().includes(query) || n.kids.some(matches);
  return nodes.filter(matches).map((n) => {
    const isUsed = used.has(n.lib);
    return (
      <React.Fragment key={n.id}>
        <div className={`lr${isUsed ? " used" : ""}`} style={{ paddingLeft: 8 + (depth - 1) * 16 }}>
          {n.sys && <span className="sysm">▣</span>}
          <span className="tname">{n.name}</span>
          {isUsed ? (
            <span className="ok" title="უკვე სტრუქტურაშია">
              ✓
            </span>
          ) : (
            <button type="button" className="ra" style={{ visibility: "visible" }} onClick={() => onAdd(n, depth)} title="დამატება">
              +
            </button>
          )}
        </div>
        {n.kids.length > 0 && <LibraryRows nodes={n.kids} depth={depth + 1} used={used} query={query} onAdd={onAdd} />}
      </React.Fragment>
    );
  });
}
